<template>
    <div class="sf-result">
        <header class="sf-result__header">
            <h2 class="sf-result__title">Jouw verhaal</h2>

            <div class="sf-result__advice" v-if="adviceText">
                <p class="sf-result__advice-text">
                    {{ adviceText }}
                </p>
                <div class="sf-result__share-action">
                    <button
                        type="button"
                        class="sf-result__share-btn"
                        @click="openShareModal"
                    >
                        Verhaal delen met reisgenoten
                    </button>
                </div>
            </div>
        </header>

        <div class="sf-result__groups container" v-if="activeGroups.length">
            <section
                v-for="group in activeGroups"
                :key="group.category"
                class="sf-result__group"
            >
                <h3 class="sf-result__group-title">
                    {{ group.category }}
                </h3>
                <p v-if="group.advice" class="sf-result__group-advice">
                    {{ group.advice }}
                </p>
                <div class="sf-result__cards">
                    <storyfinder-card
                        v-for="card in group.cards"
                        :key="card.nid"
                        :card="card"
                    />
                </div>
            </section>
        </div>

        <div class="sf-result__actions">
            <button class="sf-result__restart" @click="$emit('restart')">
                Opnieuw beginnen
            </button>
        </div>

        <modal
            :id="modalId"
            :name="modalId"
            width="90%"
            :maxWidth="650"
            :adaptive="true"
            height="auto"
            :scrollable="true"
            @before-close="handleModalClose"
        >
            <div class="modal-inner-wrapper sf-result__modal-inner">
                <button
                    type="button"
                    class="close-button"
                    @click="closeShareModal"
                    aria-label="Sluiten"
                >
                    <i class="icon-close-thin"></i>
                </button>
                <h3 class="sf-result__modal-title">Verhaal delen met reisgenoten</h3>
                <div class="sf-result__modal-content">
                    <p class="sf-result__modal-text">
                        {{ sharedStoryText }}
                    </p>
                    <div class="sf-result__modal-actions">
                        <button
                            type="button"
                            class="sf-result__copy-btn"
                            :class="{ 'is-copied': copied }"
                            @click="copyAdviceText"
                        >
                            {{ copied ? 'Gekopieerd!' : 'Kopieer tekst' }}
                        </button>
                    </div>
                </div>
            </div>
        </modal>
    </div>
</template>

<script>
import StoryfinderCard from './StoryfinderCard.vue';

export default {
  name: 'StoryfinderResult',

  components: {
    StoryfinderCard,
  },

  props: {
    persona: { type: Object, required: true },
    adviceText: { type: String, default: '' },
    storyText: { type: String, default: '' },
    groups: { type: Array, default: () => [] },
  },

  emits: ['restart'],

  data() {
    return {
      modalId: 'sf-share-story-modal',
      copied: false,
      copyTimeout: null,
    };
  },

  computed: {
    sharedStoryText() {
      return this.storyText || this.adviceText;
    },
    activeGroups() {
      if (!Array.isArray(this.groups)) return [];
      return this.groups.filter(group => group && Array.isArray(group.cards) && group.cards.length > 0);
    },
  },

  beforeDestroy() {
    if (this.copyTimeout) {
      clearTimeout(this.copyTimeout);
    }
  },

  methods: {
    openShareModal() {
      this.copied = false;
      document.documentElement.style.overflowY = 'hidden';
      document.documentElement.style.height = '100vh';
      this.$modal.show(this.modalId);
    },

    closeShareModal() {
      this.$modal.hide(this.modalId);
    },

    handleModalClose() {
      document.documentElement.style.overflowY = 'auto';
      document.documentElement.style.height = 'auto';
      this.copied = false;
    },

    async copyAdviceText() {
      const textToCopy = this.sharedStoryText;
      if (!textToCopy) return;

      let success = false;
      if (navigator && navigator.clipboard && window.isSecureContext) {
        try {
          await navigator.clipboard.writeText(textToCopy);
          success = true;
        } catch (err) {
          success = false;
        }
      }

      if (!success) {
        try {
          const textArea = document.createElement('textarea');
          textArea.value = textToCopy;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          textArea.style.top = '-999999px';
          textArea.setAttribute('readonly', '');
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
          success = true;
        } catch (err) {
          console.error('Kopiëren is niet gelukt', err);
        }
      }

      if (success) {
        this.copied = true;
        if (this.copyTimeout) {
          clearTimeout(this.copyTimeout);
        }
        this.copyTimeout = setTimeout(() => {
          this.copied = false;
        }, 3000);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.sf-result {
    min-height: 80vh;
    padding-bottom: 48px;

    &__header {
        padding: 4rem 1rem 3rem;
        text-align: center;
    }

    &__title {
        color: #fff;
        margin: 0;
    }

    &__advice {
        max-width: 600px;
        margin: 16px auto 0;
    }

    &__advice-text {
        font-family: 'Comfortaa', sans-serif;
        font-size: 15px;
        color: #fff;
        line-height: 1.6;
        margin: 0 0 1.5rem;
        text-align: center;
    }

    &__share-action {
        margin-top: 16px;
    }

    &__share-btn {
        background-color: #fff;
        color: #23201e;
        border: none;
        border-radius: 24px;
        padding: 10px 24px;
        font-family: 'Comfortaa', sans-serif;
        font-size: 15px;
        font-weight: 700;
        cursor: pointer;
        transition: background-color 0.2s ease, transform 0.1s ease, color 0.2s ease;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

        &:hover {
            background-color: #e8ddc9;
            color: #23201e;
        }

        &:active {
            transform: scale(0.98);
        }

        &:focus-visible {
            outline: 2px solid #fff;
            outline-offset: 2px;
        }
    }

    &__modal-inner {
        padding: 2rem;
        background: #fff;
        border-radius: 12px;
        position: relative;

        .close-button {
            position: absolute;
            top: 1.25rem;
            right: 1.25rem;
            background: none;
            border: none;
            cursor: pointer;
            padding: 4px;
            color: #23201e;
            font-size: 20px;
            line-height: 1;

            &:hover {
                color: #517b86;
            }
        }
    }

    &__modal-title {
        font-family: 'Comfortaa', sans-serif;
        font-size: 22px;
        font-weight: 700;
        color: #23201e;
        margin: 0 0 16px;
        padding-right: 30px;
    }

    &__modal-content {
        margin-top: 16px;
    }

    &__modal-text {
        font-family: 'Comfortaa', sans-serif;
        font-size: 15px;
        line-height: 1.6;
        color: #23201e;
        background-color: #f4ecdf;
        padding: 1rem;
        border-radius: 0.5rem;
        margin: 0 0 20px;
    }

    &__modal-actions {
        display: flex;
        justify-content: flex-end;
    }

    &__copy-btn {
        background-color: #517b86;
        color: #fff;
        border: none;
        border-radius: 24px;
        padding: 10px 24px;
        font-family: 'Comfortaa', sans-serif;
        font-size: 14px;
        font-weight: 700;
        cursor: pointer;
        transition: background-color 0.2s ease, transform 0.1s ease;

        &:hover {
            background-color: #3d6370;
        }

        &:active {
            transform: scale(0.98);
        }

        &:focus-visible {
            outline: 2px solid #517b86;
            outline-offset: 2px;
        }

        &.is-copied {
            background-color: #2e7d32;
        }
    }

    &__groups {
        padding: 1rem;
    }

    &__group {
        margin-bottom: 48px;

        &:last-child {
            margin-bottom: 24px;
        }
    }

    &__group-title {
        font-family: 'Comfortaa', sans-serif;
        font-size: 24px;
        font-weight: 700;
        color: #fff;
        margin: 0 0 8px;
    }

    &__group-advice {
        font-family: 'Comfortaa', sans-serif;
        font-size: 15px;
        line-height: 1.6;
        color: #fff;
        margin: 0 0 20px;
        max-width: 650px;
    }

    &__cards {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
        align-items: stretch;
        gap: 1rem;
        overflow-x: auto;
        padding-bottom: 1rem;
        scroll-snap-type: x mandatory;
        -webkit-overflow-scrolling: touch;

        &::-webkit-scrollbar {
            height: 4px;
        }

        &::-webkit-scrollbar-thumb {
            background: #b5afa5;
            border-radius: 4px;
        }
    }

    &__actions {
        display: flex;
        justify-content: center;
        padding: 16px;
    }

    &__restart {
        background: none;
        border: 1.5px solid #fff;
        border-radius: 24px;
        padding: 10px 24px;
        font-family: 'Comfortaa', sans-serif;
        font-size: 14px;
        color: #fff;
        cursor: pointer;
        transition: background 0.2s;

        &:hover {
            background: #23201e;
            color: #fff;
        }

        &:focus-visible {
            outline: 2px solid #517b86;
            outline-offset: 2px;
        }
    }
}
</style>
